import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, FormSpy } from 'react-final-form';
import { isEqual } from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
import {
  setCreationResetButtonIsDisabled,
  toggleAuthWindow,
  toggleAddNewItemModal
} from '../../actions';
import AddNewItemForm from './AddNewItemForm';
import { useNewItemLocalState } from '../../hooks/useNewItemLocalState';
import { userDataQuery } from '../../queries/profile';
import { createItemQuery } from '../../queries/mutations';
import Bugsnag from '../../utils/bugsnag';
import {
  FORM_VALUES,
  INITIAL_FORM_VALUES
} from '../../utils/helpers/constants';
import { convertFormToQueryInput } from '../../utils/helpers/convertFormToQueryInput';
import {
  checkIsFlatCategory,
  getCategoryValues,
  validateFunc
} from '../../utils/helpers/common';
import { validatorsConfig } from '../../utils/helpers/validateItemForm';
import { categoriesQuery } from '../../queries';

const ItemCreationForm = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [createItem] = useMutation(createItemQuery);
  const [errorText, setErrorText] = useState();
  const [failedModalIsOpen, setFailedModalIsOpen] = useState(false);

  const validate = validateFunc(t, validatorsConfig);
  const { newItemFirstModalValue, newItemSecondModalValue, clearNewItemLS } =
    useNewItemLocalState();

  const { data: dataCategories, loading } = useQuery(categoriesQuery);
  const categories = loading ? [] : dataCategories?.categories;
  const categoryId = newItemSecondModalValue || '2';
  const valueWithFlat = checkIsFlatCategory(categoryId) ? '1' : categoryId;
  const typeEstate = categories?.find(el => el.id === valueWithFlat)?.name;

  const categoryValues = getCategoryValues({ id: categoryId, categories });

  const { data } = useQuery(userDataQuery, {
    ssr: false,
    fetchPolicy: 'network-only'
  });

  const toggleFailedModal = () => setFailedModalIsOpen(!failedModalIsOpen);

  const handleErrors = result => {
    if (result?.errors?.[0]?.message) {
      setErrorText(result.errors[0].message);
    }
    Bugsnag.notify(`Status ${result.status} in create new item`, result);
    toggleFailedModal();
  };

  const createNewItem = ({ accessToken, values, setLoading = () => null }) => {
    createItem({
      variables: convertFormToQueryInput(values)
    })
      .then(res => {
        const data = res.data.createItem;
        if (data.status === 'SUCCESS') {
          const newItemId = data?.item?.id;
          const accessTokenParam = accessToken
            ? `?access_token=${accessToken}`
            : '';
          const redirectUrl = `${window.location.origin}/items/${newItemId}${accessTokenParam}`;
          router.replace(redirectUrl).then(() => {
            dispatch(toggleAddNewItemModal(false));
            clearNewItemLS();
            setLoading(false);
          });
        } else if (data.status === 'FAILURE' || data.status === 'INVALID') {
          handleErrors(data);
          setLoading(false);
          dispatch(setCreationResetButtonIsDisabled(false));
        }
      })
      .catch(error => {
        setLoading(false);
        dispatch(setCreationResetButtonIsDisabled(false));
        handleErrors({ status: 'UNHANDLED', errors: [error] });
      });
  };

  const onSubmit = async values => {
    if (data?.profile) {
      createNewItem({ values });
    } else {
      dispatch(toggleAuthWindow(true));
    }
  };

  const changeFormHandler = ({ values }) => {
    const profileValues = data?.profile
      ? {
          [FORM_VALUES.email]: data.profile.email,
          [FORM_VALUES.company]: data.profile.name,
          [FORM_VALUES.phone]: data.profile.phone?.replace(/[^\d]/g, '') ?? ''
        }
      : {};
    const initialValues = {
      ...INITIAL_FORM_VALUES,
      ...profileValues
    };

    const isValuesEqual = isEqual(initialValues, values);
    setTimeout(
      () => dispatch(setCreationResetButtonIsDisabled(isValuesEqual)),
      0
    );
  };

  useEffect(() => {
    dispatch(toggleAddNewItemModal(false));
  }, []);

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      subscription={{
        submitting: true,
        values: true
      }}
      initialValues={{
        [FORM_VALUES.cityId]: '1',
        [FORM_VALUES.owner]: 'OWNER',
        [FORM_VALUES.hasMortgage]: false,
        [FORM_VALUES.hasBillOfSale]: false,
        [FORM_VALUES.categoryId]: categoryId,
        [FORM_VALUES.typeEstate]: typeEstate,
        [FORM_VALUES.categoryValues]: categoryValues,
        [FORM_VALUES.leased]: newItemFirstModalValue || 'false',
        [FORM_VALUES.cityHasLocations]: true
      }}
    >
      {renderProps => (
        <form
          id="new_ad"
          method="get"
          acceptCharset="UTF-8"
          className="search-bar__form"
          onSubmit={renderProps.handleSubmit}
        >
          <FormSpy
            onChange={changeFormHandler}
            subscription={{ values: true }}
          />
          <AddNewItemForm
            profile={data?.profile}
            errorText={errorText}
            categories={categories}
            setErrorText={setErrorText}
            createNewItem={createNewItem}
            toggleFailedModal={toggleFailedModal}
            failedModalIsOpen={failedModalIsOpen}
          />
        </form>
      )}
    </Form>
  );
};

export default ItemCreationForm;
